import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { CodeFromEmail } from '../components/LandingPage/ResetPassword/CodeFromEmail'
import { ResetPassword } from '../components/LandingPage/ResetPassword/ResetPassword'
import { useSecureAccountMutation } from '../features/auth/passwordSlice'
import toast from 'react-hot-toast'

export const SecureAccount = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [step, setStep] = useState<'loading' | 'code' | 'password'>('loading')
  const [email, setEmail] = useState<string>('')
  const [code, setCode] = useState<number | null>(null)
  const [secureAccount, { isLoading }] = useSecureAccountMutation()

  useEffect(() => {
    const token = searchParams.get('token')
    
    if (!token) {
      toast.error('Invalid security token')
      navigate('/login')
      return
    }

    // Immediately call secure account API
    handleSecureAccount(token)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSecureAccount = async (token: string) => {
    try {
      toast.loading('Securing your account...')
      const result = await secureAccount({ token }).unwrap()
      toast.dismiss()
      
      // Show security alert with actions
      toast.success(
        `Account secured! ${result.actions.join(' ')}`,
        {
          duration: 5000,
          icon: '🔒',
        }
      )

      // Set email and move to code step
      setEmail(result.email)
      setStep('code')
    } catch (error: any) {
      toast.dismiss()
      const errorMessage = error?.data?.message || 'Failed to secure account'
      toast.error(errorMessage)
      
      // Redirect to login on error
      setTimeout(() => {
        navigate('/login')
      }, 2000)
    }
  }

  // Wrapper to convert step type for CodeFromEmail component
  const handleSetStep: Dispatch<SetStateAction<'email' | 'code' | 'password'>> = (value) => {
    setStep((prev) => {
      const currentStep = prev === 'loading' ? 'code' : prev
      const nextStep = typeof value === 'function' ? value(currentStep) : value

      if (nextStep === 'email') {
        // Shouldn't happen, but if it does, go back to code
        return 'code'
      }

      return nextStep
    })
  }

  const _renderForm = () => {
    if (step === 'loading') {
      return (
        <div className='bg-white w-full max-w-[400px] rounded-md shadow-lg px-5 py-10'>
          <h3 className='text-2xl text-indigo-500 font-semibold text-center'>
            Gcbuying
          </h3>
          <div className='mt-8 text-center'>
            <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500'></div>
            <p className='text-sm text-slate-600 mt-4'>Securing your account...</p>
            <p className='text-xs text-slate-500 mt-2'>
              Logging out all sessions and sending password reset code...
            </p>
          </div>
        </div>
      )
    } else if (step === 'code') {
      return <CodeFromEmail email={email} setCode={setCode} setStep={handleSetStep} />
    } else if (step === 'password') {
      return <ResetPassword email={email} setStep={handleSetStep} code={code} />
    }
  }

  return (
    <div className='login-bg1 w-full font-dashboard bg-[#f8f8f8] h-screen flex justify-center items-center'>
      {_renderForm()}
    </div>
  )
}

